import { authRequest } from './client';
import { type UpdateWithdrawalLimitInput, type WithdrawalLimitItem } from './types';

// ─── backoffice-api withdrawal-limits client ───────────────────────────────
//
// Thin fetch wrappers around backoffice-api's `/v1/withdrawal-limits`
// endpoints (Feature 030 Task 6). Reads (`GET`) are FINANCE_VIEWER OR
// CONFIG_MANAGER; writes (`PATCH`/`PUT`/`DELETE`) are CONFIG_MANAGER-only —
// mirrors gas-station.ts's read/write role split. `clearWithdrawalLimitOverride`
// returns `void`: `DELETE /withdrawal-limits/:merchantId` is `@HttpCode(200)`
// with no body.

export function getDefaultWithdrawalLimit(): Promise<{ data: WithdrawalLimitItem }> {
  return authRequest<{ data: WithdrawalLimitItem }>('/withdrawal-limits');
}

export function updateDefaultWithdrawalLimit(
  input: UpdateWithdrawalLimitInput,
): Promise<{ data: WithdrawalLimitItem }> {
  return authRequest<{ data: WithdrawalLimitItem }>('/withdrawal-limits', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function getResolvedWithdrawalLimit(merchantId: string): Promise<{ data: WithdrawalLimitItem }> {
  return authRequest<{ data: WithdrawalLimitItem }>(`/withdrawal-limits/${merchantId}`);
}

export function setWithdrawalLimitOverride(
  merchantId: string,
  input: UpdateWithdrawalLimitInput,
): Promise<{ data: WithdrawalLimitItem }> {
  return authRequest<{ data: WithdrawalLimitItem }>(`/withdrawal-limits/${merchantId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function clearWithdrawalLimitOverride(merchantId: string): Promise<void> {
  try {
    await authRequest<unknown>(`/withdrawal-limits/${merchantId}`, { method: 'DELETE' });
  } catch (err) {
    // This route is deliberately `@HttpCode(200)`, not 204 (see the
    // controller's doc comment) — a 200 with an empty body. `client.ts`'s
    // `authRequest` only special-cases 204 before calling `res.json()`, so a
    // genuinely empty-but-successful body here throws a SyntaxError from
    // `JSON.parse('')`. A real backend failure is a non-2xx response, which
    // `fetchRaw` turns into an `ApiError` *before* `res.json()` ever runs —
    // so swallowing only `SyntaxError` here can never mask a genuine error.
    if (!(err instanceof SyntaxError)) throw err;
  }
}
